import { forwardRef, InputHTMLAttributes, useCallback, useRef, useState } from 'react';

/**
 * DatePicker component matching AngularJS uib-datepicker-popup styles
 *
 * This is a simple input-based date picker matching the AngularJS version.
 * Uses native HTML5 date input for modern browsers with fallback to text input.
 *
 * Source of truth from AngularJS uib-datepicker-popup:
 * - Input field with calendar icon button
 * - Format: MM/DD/YYYY
 * - Bootstrap .form-control styling
 * - Calendar button with .btn .btn-default styling
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiDatePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value'
> {
  hasError?: boolean;
  value?: string; // MM/DD/YYYY format
  onChange?: (value: string) => void;
  onBlur?: () => void;
  helperText?: string;
  helperTextClassName?: string;
  /** Extra classes applied to the text `<input>` element itself (not the wrapper div). */
  inputClassName?: string;
}

export const UiDatePicker = forwardRef<HTMLInputElement, UiDatePickerProps>(
  (
    {
      hasError,
      className = '',
      value = '',
      onChange,
      onBlur,
      helperText,
      helperTextClassName,
      inputClassName = '',
      disabled,
      readOnly,
      min,
      max,
      ...props
    },
    _ref
  ) => {
    const [, setShowCalendar] = useState(false);
    const textInputRef = useRef<HTMLInputElement>(null);
    const dateInputRef = useRef<HTMLInputElement>(null);

    // Input base classes matching .form-control from Bootstrap
    const baseClasses = [
      'block',
      'w-full',
      'h-angular-input', // height: 34px
      'px-3',
      'py-[6px]',
      'text-angular-base',
      'leading-angular-base',
      'text-[#555]',
      'bg-white',
      'border',
      'border-solid',
      'rounded-angular',
      'transition-all',
      'duration-150',
      'ease-in-out',
    ].join(' ');

    const borderClasses = hasError
      ? 'border-angular-error-highlight'
      : 'border-angular-border-input focus:border-angular-focus';

    const shadowClasses = hasError
      ? 'focus:shadow-angular-input-error-focus'
      : 'shadow-angular-input focus:shadow-angular-input-focus';

    const disabledClasses =
      disabled || readOnly
        ? hasError
          ? '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100'
          : '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100 !border-angular-border-input'
        : '';

    const focusClasses = 'focus:outline-none';

    // Button matching .btn .btn-default from Bootstrap
    const buttonBaseClasses = [
      'h-angular-input', // height: 34px to match input
      'px-3',
      'text-angular-base',
      'bg-white',
      'border',
      'border-solid',
      'border-angular-border-input',
      'rounded-angular',
      'transition-all',
      'duration-150',
      'hover:bg-angular-bg-hover', // #e6e6e6
      'active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]',
    ].join(' ');

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange?.(newValue);
      },
      [onChange]
    );

    const handleCalendarClick = useCallback(() => {
      if (disabled) return;
      // Trigger native date picker on the hidden date input
      dateInputRef.current?.showPicker?.();
      setShowCalendar(true);
    }, [disabled]);

    const handleNativeDateChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value) {
          onChange?.('');
          return;
        }
        // Convert YYYY-MM-DD to MM/DD/YYYY
        const [year, month, day] = e.target.value.split('-');
        const formattedDate = `${month}/${day}/${year}`;
        onChange?.(formattedDate);
      },
      [onChange]
    );

    // Convert MM/DD/YYYY to YYYY-MM-DD for native date input
    const toNativeDateValue = (dateStr: string): string => {
      if (!dateStr) return '';
      try {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [month, day, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      } catch {
        return '';
      }
      return '';
    };

    return (
      <div className={`w-full ${className}`}>
        <div className='flex relative'>
          <input
            ref={textInputRef}
            type='text'
            // AngularJS uses lowercase `mm/dd/yyyy` consistently across all date inputs
            // (e.g. taskActivity.component.js:67,92,142,167,427). Keep the React default
            // in sync so the form looks identical everywhere.
            placeholder='mm/dd/yyyy'
            className={`${baseClasses} ${borderClasses} ${shadowClasses} ${disabledClasses} ${focusClasses} rounded-r-none border-r-0 ${inputClassName}`}
            style={disabled ? { background: '#eee' } : undefined}
            value={value}
            onChange={handleInputChange}
            onBlur={onBlur}
            disabled={disabled}
            readOnly={readOnly}
            min={min}
            max={max}
            {...props}
          />
          {/* Hidden native date input for calendar picker */}
          <input
            ref={dateInputRef}
            type='date'
            value={toNativeDateValue(value)}
            onChange={handleNativeDateChange}
            min={min}
            max={max}
            tabIndex={-1}
            aria-hidden='true'
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: 0,
              height: 0,
            }}
          />
          <button
            type='button'
            className={`${buttonBaseClasses} rounded-l-none flex-shrink-0 ${disabled ? 'cursor-not-allowed' : ''}`}
            style={disabled ? { background: '#acacac' } : undefined}
            onClick={handleCalendarClick}
            disabled={disabled}
            tabIndex={-1}
          >
            <i className='fa fa-calendar marginr0' aria-hidden='true' />
          </button>
        </div>
        {helperText && (
          <p
            className={`help-block mt-1 ${helperTextClassName ?? 'text-xs'} ${hasError ? 'text-angular-error-border' : 'text-angular-text-muted'}`}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

UiDatePicker.displayName = 'UiDatePicker';
